// ─── Website Tracking section (Marketing tab) ────────────────────────────────
// Self-service embeddable visitor tracking: dealer enters their website URL,
// gets a stable copyable snippet, and sees visitor analytics once installed.
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip,
  ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  Globe, Copy, Mail, CheckCircle2, Clock, AlertTriangle, RefreshCw,
  Loader2, Trash2, KeyRound, BarChart3,
} from "lucide-react";

interface TrackedSiteDto {
  id: string;
  websiteUrl: string;
  approvedDomain: string;
  siteKey: string;
  status: "pending" | "connected" | "stale" | "revoked";
  lastEventAt: string | null;
  lastRejectedReason: string | null;
  lastRejectedAt: string | null;
}

interface SummaryDto {
  days: number;
  sessions: number;
  pageviews: number;
  visitors: number;
  leadSessions: number;
  daily: Array<{ date: string; sessions: number }>;
  monthly: Array<{ month: string; sessions: number }>;
  baselineBeforeDays?: number;
  baselineBeforeMonths?: number;
  topSources: Array<{ label: string; count: number }>;
  sourceTotals?: Array<{ label: string; count: number; leads: number }>;
  sourceMonthly?: Array<{ source: string; month: string; sessions: number }>;
  mediums: Array<{ label: string; count: number }>;
  topPages: Array<{ label: string; count: number }>;
  topCampaigns: Array<{ label: string; count: number }>;
}

const FAKE_SUMMARY: SummaryDto = {
  days: 30, sessions: 412, pageviews: 1364, visitors: 351, leadSessions: 23,
  daily: Array.from({ length: 30 }, (_, i) => ({
    date: `${i + 1}`,
    sessions: [9, 12, 8, 15, 11, 18, 14, 10, 16, 13, 12, 17, 9, 14, 20, 15, 11, 13, 18, 12, 16, 10, 14, 19, 13, 15, 11, 17, 12, 16][i],
  })),
  monthly: [
    { month: "2025-09", sessions: 180 }, { month: "2025-10", sessions: 260 }, { month: "2025-11", sessions: 310 },
    { month: "2025-12", sessions: 360 }, { month: "2026-01", sessions: 420 }, { month: "2026-02", sessions: 300 },
    { month: "2026-03", sessions: 380 }, { month: "2026-04", sessions: 450 }, { month: "2026-05", sessions: 520 },
    { month: "2026-06", sessions: 610 }, { month: "2026-07", sessions: 680 }, { month: "2026-08", sessions: 380 },
  ],
  topSources: [
    { label: "Google", count: 176 }, { label: "Facebook", count: 92 },
    { label: "Direct", count: 74 }, { label: "cars.com", count: 41 }, { label: "Instagram", count: 29 },
  ],
  mediums: [
    { label: "organic", count: 195 }, { label: "social", count: 121 },
    { label: "direct", count: 74 }, { label: "referral", count: 22 },
  ],
  topPages: [
    { label: "/inventory", count: 168 }, { label: "/", count: 122 },
    { label: "/financing", count: 64 }, { label: "/inventory/2021-f-150", count: 38 },
  ],
  topCampaigns: [{ label: "spring-sale", count: 47 }, { label: "truck-month", count: 21 }],
  baselineBeforeDays: 118,
  baselineBeforeMonths: 0,
  sourceTotals: [
    { label: "google", count: 2214, leads: 23 },
    { label: "direct", count: 602, leads: 13 },
    { label: "facebook", count: 602, leads: 7 },
    { label: "cars.com", count: 431, leads: 14 },
    { label: "instagram", count: 407, leads: 11 },
    { label: "autotrader", count: 334, leads: 13 },
    { label: "bing", count: 260, leads: 4 },
  ],
  sourceMonthly: [
    { source: "google", month: "2025-09", sessions: 84 }, { source: "google", month: "2025-10", sessions: 119 }, { source: "google", month: "2025-11", sessions: 143 }, { source: "google", month: "2025-12", sessions: 164 }, { source: "google", month: "2026-01", sessions: 192 }, { source: "google", month: "2026-02", sessions: 137 }, { source: "google", month: "2026-03", sessions: 174 }, { source: "google", month: "2026-04", sessions: 205 }, { source: "google", month: "2026-05", sessions: 236 }, { source: "google", month: "2026-06", sessions: 278 }, { source: "google", month: "2026-07", sessions: 308 }, { source: "google", month: "2026-08", sessions: 174 },
    { source: "direct", month: "2025-09", sessions: 22 }, { source: "direct", month: "2025-10", sessions: 32 }, { source: "direct", month: "2025-11", sessions: 38 }, { source: "direct", month: "2025-12", sessions: 45 }, { source: "direct", month: "2026-01", sessions: 52 }, { source: "direct", month: "2026-02", sessions: 37 }, { source: "direct", month: "2026-03", sessions: 47 }, { source: "direct", month: "2026-04", sessions: 56 }, { source: "direct", month: "2026-05", sessions: 65 }, { source: "direct", month: "2026-06", sessions: 76 }, { source: "direct", month: "2026-07", sessions: 85 }, { source: "direct", month: "2026-08", sessions: 47 },
    { source: "facebook", month: "2025-09", sessions: 22 }, { source: "facebook", month: "2025-10", sessions: 32 }, { source: "facebook", month: "2025-11", sessions: 38 }, { source: "facebook", month: "2025-12", sessions: 45 }, { source: "facebook", month: "2026-01", sessions: 52 }, { source: "facebook", month: "2026-02", sessions: 37 }, { source: "facebook", month: "2026-03", sessions: 47 }, { source: "facebook", month: "2026-04", sessions: 56 }, { source: "facebook", month: "2026-05", sessions: 65 }, { source: "facebook", month: "2026-06", sessions: 76 }, { source: "facebook", month: "2026-07", sessions: 85 }, { source: "facebook", month: "2026-08", sessions: 47 },
    { source: "cars.com", month: "2025-09", sessions: 16 }, { source: "cars.com", month: "2025-10", sessions: 23 }, { source: "cars.com", month: "2025-11", sessions: 27 }, { source: "cars.com", month: "2025-12", sessions: 32 }, { source: "cars.com", month: "2026-01", sessions: 37 }, { source: "cars.com", month: "2026-02", sessions: 27 }, { source: "cars.com", month: "2026-03", sessions: 34 }, { source: "cars.com", month: "2026-04", sessions: 40 }, { source: "cars.com", month: "2026-05", sessions: 46 }, { source: "cars.com", month: "2026-06", sessions: 54 }, { source: "cars.com", month: "2026-07", sessions: 61 }, { source: "cars.com", month: "2026-08", sessions: 34 },
    { source: "instagram", month: "2025-09", sessions: 15 }, { source: "instagram", month: "2025-10", sessions: 22 }, { source: "instagram", month: "2025-11", sessions: 26 }, { source: "instagram", month: "2025-12", sessions: 30 }, { source: "instagram", month: "2026-01", sessions: 35 }, { source: "instagram", month: "2026-02", sessions: 25 }, { source: "instagram", month: "2026-03", sessions: 32 }, { source: "instagram", month: "2026-04", sessions: 38 }, { source: "instagram", month: "2026-05", sessions: 44 }, { source: "instagram", month: "2026-06", sessions: 51 }, { source: "instagram", month: "2026-07", sessions: 57 }, { source: "instagram", month: "2026-08", sessions: 32 },
    { source: "autotrader", month: "2025-09", sessions: 12 }, { source: "autotrader", month: "2025-10", sessions: 18 }, { source: "autotrader", month: "2025-11", sessions: 21 }, { source: "autotrader", month: "2025-12", sessions: 25 }, { source: "autotrader", month: "2026-01", sessions: 29 }, { source: "autotrader", month: "2026-02", sessions: 21 }, { source: "autotrader", month: "2026-03", sessions: 26 }, { source: "autotrader", month: "2026-04", sessions: 31 }, { source: "autotrader", month: "2026-05", sessions: 36 }, { source: "autotrader", month: "2026-06", sessions: 42 }, { source: "autotrader", month: "2026-07", sessions: 47 }, { source: "autotrader", month: "2026-08", sessions: 26 },
    { source: "bing", month: "2025-09", sessions: 9 }, { source: "bing", month: "2025-10", sessions: 14 }, { source: "bing", month: "2025-11", sessions: 17 }, { source: "bing", month: "2025-12", sessions: 19 }, { source: "bing", month: "2026-01", sessions: 23 }, { source: "bing", month: "2026-02", sessions: 16 }, { source: "bing", month: "2026-03", sessions: 20 }, { source: "bing", month: "2026-04", sessions: 24 }, { source: "bing", month: "2026-05", sessions: 28 }, { source: "bing", month: "2026-06", sessions: 33 }, { source: "bing", month: "2026-07", sessions: 37 }, { source: "bing", month: "2026-08", sessions: 20 },
  ],
};

const SOURCE_COLORS = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#3b82f6", "#ec4899", "#14b8a6", "#8b5cf6", "#f97316", "#64748b"];

function monthLabel(key: string, withYear = true) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString(undefined, withYear ? { month: "short", year: "numeric" } : { month: "short" });
}

type ChartRange = 7 | 30 | 90 | "12m";

// Turn the server's zero-filled per-period series into a running total,
// starting from the all-time count before the window (like the reference
// chart). The server owns bucket keys and boundaries; we only format labels.
function buildCumulative(data: SummaryDto, range: ChartRange) {
  let points: Array<{ label: string; sessions: number }>;
  if (range === "12m") {
    points = data.monthly.map((m) => {
      const [y, mo] = m.month.split("-").map(Number);
      return {
        label: new Date(y, (mo || 1) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" }),
        sessions: m.sessions,
      };
    });
  } else {
    points = data.daily.map((d) => {
      const parts = d.date.split("-").map(Number);
      return {
        label: parts.length === 3
          ? new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString(undefined, { month: "short", day: "numeric" })
          : d.date,
        sessions: d.sessions,
      };
    });
  }
  let running = (range === "12m" ? data.baselineBeforeMonths : data.baselineBeforeDays) ?? 0;
  return points.map((p) => ({ label: p.label, total: (running += p.sessions) }));
}

function BigRankedList({ items, unit }: { items: Array<{ label: string; count: number }>; unit: string }) {
  const top = items[0]?.count || 1;
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-4">
          <span className="flex-1 text-sm truncate">{item.label}</span>
          <span className="text-sm font-semibold tabular-nums whitespace-nowrap">{item.count.toLocaleString()} {unit}</span>
          <div className="w-24 h-2 bg-muted rounded-full overflow-hidden shrink-0">
            <div className="h-full bg-foreground rounded-full" style={{ width: `${(item.count / top) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function AnalyticsDashboard({ data, sample, range = "12m" }: { data: SummaryDto; sample?: boolean; range?: ChartRange }) {
  const { t } = useTranslation(["operations"]);
  const [showAllSources, setShowAllSources] = useState(false);
  const [chartView, setChartView] = useState<"combined" | "compare">("combined");
  const [compareMode, setCompareMode] = useState<"totals" | "share">("totals");
  const [hiddenSources, setHiddenSources] = useState<Set<string>>(new Set());
  // The by-month table starts scrolled to the newest month (rightmost column);
  // the dealer can scroll back in time from there.
  const byMonthScrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = byMonthScrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [data]);
  const cumulative = useMemo(() => buildCumulative(data, range), [data, range]);
  const totalVisits = cumulative.length ? cumulative[cumulative.length - 1].total : 0;

  const monthKeys = useMemo(() => data.monthly.map((m) => m.month), [data.monthly]);
  const sourceTotals = data.sourceTotals ?? [];
  const sourceMonthMap = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const r of data.sourceMonthly ?? []) {
      if (!map.has(r.source)) map.set(r.source, new Map());
      map.get(r.source)!.set(r.month, r.sessions);
    }
    return map;
  }, [data.sourceMonthly]);
  const colorOf = (i: number) => SOURCE_COLORS[i % SOURCE_COLORS.length];

  // Compare view: defaults to the last 12 months summed, or a single month.
  const [compareMonth, setCompareMonth] = useState<string>("all");
  const compareData = useMemo(() => {
    const sums = new Map<string, number>();
    for (const r of data.sourceMonthly ?? []) {
      if (compareMonth !== "all" && r.month !== compareMonth) continue;
      sums.set(r.source, (sums.get(r.source) ?? 0) + r.sessions);
    }
    return Array.from(sums, ([label, count]) => ({ label, count }))
      .filter((r) => r.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [data, compareMonth]);
  const compareColor = (label: string) => {
    const i = sourceTotals.findIndex((s) => s.label === label);
    return colorOf(i >= 0 ? i : compareData.findIndex((r) => r.label === label));
  };
  const visibleCompare = compareData.filter((r) => !hiddenSources.has(r.label));
  const visibleTotal = visibleCompare.reduce((s, r) => s + r.count, 0);
  const toggleSource = (label: string) =>
    setHiddenSources((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  const tooltipStyle = {
    background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
    borderRadius: 8, fontSize: 12, color: "hsl(var(--popover-foreground))",
  } as const;
  return (
    <div className={`space-y-4 ${sample ? "select-none" : ""}`}>
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: t("operations:webTracking.statVisits"), desc: t("operations:webTracking.statVisitsDesc"), value: data.sessions, testId: "text-wt-visits" },
          { label: t("operations:webTracking.statPageviews"), desc: t("operations:webTracking.statPageviewsDesc"), value: data.pageviews, testId: "text-wt-pageviews" },
          { label: t("operations:webTracking.statVisitors"), desc: t("operations:webTracking.statVisitorsDesc"), value: data.visitors, testId: "text-wt-visitors" },
        ].map(({ label, desc, value, testId }) => (
          <Tooltip key={label}>
            <TooltipTrigger asChild>
              <Card className="cursor-default">
                <CardContent className="pt-3 pb-3 px-4">
                  <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
                  <p className="text-lg font-bold tabular-nums" data-testid={testId}>{value.toLocaleString()}</p>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="max-w-[240px] text-xs">{desc}</TooltipContent>
          </Tooltip>
        ))}
      </div>

      <div className="rounded-lg border border-border/60 p-4">
        <div className="flex items-start justify-between flex-nowrap gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">
              {chartView === "combined"
                ? t("operations:webTracking.chartCumulativeTitle")
                : t("operations:webTracking.compareTitle")}
            </p>
            <p className="text-xs text-muted-foreground">
              {chartView === "combined"
                ? t("operations:webTracking.chartCumulativeSubtitle")
                : t("operations:webTracking.compareSubtitle")}
            </p>
          </div>
          {/* View toggle stays alone in the top-right so it never shifts */}
          <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5 shrink-0">
            {(["combined", "compare"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setChartView(v)}
                data-testid={`button-wt-view-${v}`}
                className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${chartView === v ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {v === "combined" ? t("operations:webTracking.viewCombined") : t("operations:webTracking.viewCompare")}
              </button>
            ))}
          </div>
        </div>

        {chartView === "combined" ? (
          <>
          <div className="flex justify-end mb-1">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="text-wt-cumulative-total">
              <span className="w-2 h-2 rounded-full bg-primary inline-block" />
              {t("operations:webTracking.totalVisits", { formatted: totalVisits.toLocaleString() })}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={cumulative} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="wtCumFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={28} />
              <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} width={44} />
              <ChartTooltip
                cursor={{ stroke: "hsl(var(--primary))", strokeWidth: 1, strokeDasharray: "4 4" }}
                formatter={(v: number) => [v.toLocaleString(), t("operations:webTracking.unitVisits")]}
                contentStyle={tooltipStyle}
              />
              <Area type="monotone" dataKey="total" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#wtCumFill)" dot={false} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
          </>
        ) : (
          <div>
            <div className="flex justify-end items-center gap-2 mb-2 flex-wrap">
              <select
                value={compareMonth}
                onChange={(e) => setCompareMonth(e.target.value)}
                data-testid="select-wt-compare-month"
                className="h-7 rounded-md border border-border/60 bg-background px-2 text-[11px] font-medium text-foreground"
              >
                <option value="all">{t("operations:webTracking.last12Months")}</option>
                {[...(data.monthly ?? [])].reverse().map((m) => (
                  <option key={m.month} value={m.month}>{monthLabel(m.month)}</option>
                ))}
              </select>
              <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5">
                {(["totals", "share"] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setCompareMode(m)}
                    data-testid={`button-wt-compare-${m}`}
                    className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${compareMode === m ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {m === "totals" ? t("operations:webTracking.modeTotals") : t("operations:webTracking.modeShare")}
                  </button>
                ))}
              </div>
            </div>
            {/* Clickable legend: hide/show a source */}
            <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-3">
              {compareData.map((s) => (
                <button
                  key={s.label}
                  onClick={() => toggleSource(s.label)}
                  data-testid={`button-wt-legend-${s.label}`}
                  className={`flex items-center gap-1.5 text-xs transition-opacity ${hiddenSources.has(s.label) ? "opacity-40 line-through" : ""}`}
                >
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: compareColor(s.label) }} />
                  {s.label}
                </button>
              ))}
            </div>
            {visibleCompare.length === 0 ? (
              <p className="text-xs text-muted-foreground py-8 text-center">{t("operations:webTracking.allSourcesHidden")}</p>
            ) : compareMode === "totals" ? (
              <ResponsiveContainer width="100%" height={Math.max(140, visibleCompare.length * 38)}>
                <BarChart data={visibleCompare} layout="vertical" margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                  <YAxis type="category" dataKey="label" width={90} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <ChartTooltip
                    cursor={{ fill: "hsl(var(--muted))" }}
                    formatter={(v: number) => [v.toLocaleString(), t("operations:webTracking.unitVisits")]}
                    contentStyle={tooltipStyle}
                  />
                  <Bar dataKey="count" radius={[0, 5, 5, 0]} barSize={20}>
                    {visibleCompare.map((s) => <Cell key={s.label} fill={compareColor(s.label)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-full sm:w-1/2">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <ChartTooltip
                        formatter={(v: number, name: string) => [
                          `${v.toLocaleString()} (${visibleTotal > 0 ? ((v / visibleTotal) * 100).toFixed(1) : 0}%)`, name,
                        ]}
                        contentStyle={tooltipStyle}
                      />
                      <Pie data={visibleCompare} dataKey="count" nameKey="label" innerRadius={0} outerRadius={90} paddingAngle={2} strokeWidth={0}>
                        {visibleCompare.map((s) => <Cell key={s.label} fill={compareColor(s.label)} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-muted-foreground text-center mt-1">{t("operations:webTracking.hoverSlice")}</p>
                </div>
                <div className="w-full sm:w-1/2 space-y-1.5">
                  {visibleCompare.map((s) => (
                    <div key={s.label} className="flex items-center gap-2 text-xs">
                      <span className="w-2 h-2 rounded-full inline-block shrink-0" style={{ background: compareColor(s.label) }} />
                      <span className="flex-1 truncate">{s.label}</span>
                      <span className="font-semibold tabular-nums">
                        {visibleTotal > 0 ? `${((s.count / visibleTotal) * 100).toFixed(1)}%` : "–"}
                      </span>
                      <span className="text-muted-foreground tabular-nums w-12 text-right">{s.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Monthly breakdown: one mini chart per source ─────────────────── */}
      {sourceTotals.length > 0 && (
        <div>
          <p className="text-sm font-semibold">{t("operations:webTracking.monthlyBreakdownTitle")}</p>
          <p className="text-xs text-muted-foreground mb-3">{t("operations:webTracking.monthlyBreakdownSubtitle")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {sourceTotals.slice(0, 6).map((s, i) => {
              const perMonth = sourceMonthMap.get(s.label);
              const series = monthKeys.map((k) => ({ label: monthLabel(k, false), sessions: perMonth?.get(k) ?? 0 }));
              return (
                <div key={s.label} className="rounded-lg border border-border/60 p-3">
                  <p className="flex items-center gap-1.5 text-xs font-semibold mb-2">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ background: colorOf(i) }} />
                    {s.label}
                  </p>
                  <ResponsiveContainer width="100%" height={110}>
                    <BarChart data={series} margin={{ top: 2, right: 2, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
                      <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} allowDecimals={false} />
                      <ChartTooltip
                        cursor={{ fill: "hsl(var(--muted))" }}
                        formatter={(v: number) => [v.toLocaleString(), t("operations:webTracking.unitVisits")]}
                        contentStyle={{
                          background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
                          borderRadius: 8, fontSize: 11, color: "hsl(var(--popover-foreground))",
                        }}
                      />
                      <Bar dataKey="sessions" fill={colorOf(i)} radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── All-time by source table ─────────────────────────────────────── */}
      {sourceTotals.length > 0 && (
        <div>
          <p className="text-sm font-semibold mb-2">{t("operations:webTracking.allTimeBySourceTitle")}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground border-b border-border/60">
                  <th className="text-left font-medium py-1.5 pr-2">{t("operations:webTracking.tableSource")}</th>
                  <th className="text-right font-medium py-1.5 pl-2">{t("operations:webTracking.tableVisits")}</th>
                </tr>
              </thead>
              <tbody>
                {(showAllSources ? sourceTotals : sourceTotals.slice(0, 6)).map((s, i) => (
                  <tr key={s.label} className="border-b border-border/40">
                    <td className="py-1.5 pr-2">
                      <span className="flex items-center gap-1.5 font-medium">
                        <span className="w-2 h-2 rounded-full inline-block shrink-0" style={{ background: colorOf(i) }} />
                        {s.label}
                      </span>
                    </td>
                    <td className="text-right tabular-nums font-semibold py-1.5 pl-2">{s.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sourceTotals.length > 6 && (
            <button
              onClick={() => setShowAllSources((v) => !v)}
              data-testid="button-wt-show-all-sources"
              className="mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {showAllSources
                ? t("operations:webTracking.showFewerSources")
                : t("operations:webTracking.showAllSources", { count: sourceTotals.length })}
            </button>
          )}
        </div>
      )}

      {/* ── By month: visits per source table ────────────────────────────── */}
      {sourceTotals.length > 0 && monthKeys.length > 0 && (
        <div>
          <p className="text-sm font-semibold mb-2">{t("operations:webTracking.byMonthTitle")}</p>
          <div className="overflow-x-auto" ref={byMonthScrollRef}>
            <table className="text-xs min-w-full">
              <thead>
                <tr className="text-muted-foreground border-b border-border/60">
                  <th className="text-left font-medium py-1.5 pr-3 whitespace-nowrap sticky left-0 bg-card">{t("operations:webTracking.tableSource")}</th>
                  {monthKeys.map((k) => (
                    <th key={k} className="text-right font-medium py-1.5 px-2 whitespace-nowrap">{monthLabel(k)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sourceTotals.slice(0, showAllSources ? undefined : 6).map((s, i) => {
                  const perMonth = sourceMonthMap.get(s.label);
                  return (
                    <tr key={s.label} className="border-b border-border/40">
                      <td className="py-1.5 pr-3 whitespace-nowrap sticky left-0 bg-card">
                        <span className="flex items-center gap-1.5 font-medium">
                          <span className="w-2 h-2 rounded-full inline-block shrink-0" style={{ background: colorOf(i) }} />
                          {s.label}
                        </span>
                      </td>
                      {monthKeys.map((k) => {
                        const v = perMonth?.get(k) ?? 0;
                        return (
                          <td key={k} className="text-right tabular-nums py-1.5 px-2">
                            {v > 0 ? v.toLocaleString() : <span className="text-muted-foreground/60">–</span>}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-border/60 p-4">
        <p className="text-sm font-semibold mb-3">{t("operations:webTracking.listPages")}</p>
        <BigRankedList items={data.topPages} unit={t("operations:webTracking.unitVisits")} />
      </div>

      <div className="rounded-lg border border-border/60 p-4">
        <p className="text-sm font-semibold mb-3">{t("operations:webTracking.listCampaigns")}</p>
        {data.topCampaigns.length > 0
          ? <BigRankedList items={data.topCampaigns} unit={t("operations:webTracking.unitVisits")} />
          : <p className="text-xs text-muted-foreground">{t("operations:webTracking.noCampaigns")}</p>}
      </div>
    </div>
  );
}

export default function WebsiteTrackingSection() {
  const { t } = useTranslation(["operations", "common"]);
  const qc = useQueryClient();
  const { toast } = useToast();
  const [urlInput, setUrlInput] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [range, setRange] = useState<ChartRange>("12m");
  const days = typeof range === "number" ? range : 30;
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [verifyResult, setVerifyResult] = useState<any>(null);
  const [showExample, setShowExample] = useState(false);

  const { data: siteData, isLoading } = useQuery<{ site: TrackedSiteDto | null }>({
    queryKey: ["/api/website-tracking/site"],
  });
  const site = siteData?.site ?? null;

  const { data: summary } = useQuery<SummaryDto>({
    queryKey: [`/api/website-tracking/summary?days=${days}`],
    enabled: !!site && (site.status === "connected" || site.status === "stale"),
  });

  const snippet = useMemo(() => {
    if (!site) return "";
    const origin = window.location.origin;
    return `<script async src="${origin}/tracker.js" data-site-key="${site.siteKey}"></script>`;
  }, [site]);

  async function createSite() {
    if (!urlInput.trim()) return;
    setBusy("create");
    try {
      await apiRequest("POST", "/api/website-tracking/site", { websiteUrl: urlInput.trim() });
      await qc.invalidateQueries({ queryKey: ["/api/website-tracking/site"] });
      toast({ title: t("operations:webTracking.createdTitle"), description: t("operations:webTracking.createdBody") });
    } catch (e: any) {
      toast({ title: t("operations:webTracking.createFailedTitle"), description: e?.message || "", variant: "destructive" });
    } finally { setBusy(null); }
  }

  function copyCode() {
    navigator.clipboard.writeText(snippet).then(
      () => toast({ title: t("operations:webTracking.copiedTitle"), description: t("operations:webTracking.copiedBody") }),
      () => toast({ title: t("operations:webTracking.copyFailedTitle"), variant: "destructive" }),
    );
  }

  function emailProvider() {
    const subject = encodeURIComponent(t("operations:webTracking.emailSubject"));
    const body = encodeURIComponent(t("operations:webTracking.emailBody", { snippet }));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  async function verifyInstall() {
    setBusy("verify");
    try {
      const res = await apiRequest("GET", "/api/website-tracking/verify");
      const data = await res.json();
      setVerifyResult(data);
      await qc.invalidateQueries({ queryKey: ["/api/website-tracking/site"] });
    } catch {
      toast({ title: t("operations:webTracking.verifyFailedTitle"), variant: "destructive" });
    } finally { setBusy(null); }
  }

  async function rotateKey() {
    setBusy("rotate");
    try {
      await apiRequest("POST", "/api/website-tracking/site/rotate-key");
      await qc.invalidateQueries({ queryKey: ["/api/website-tracking/site"] });
      toast({ title: t("operations:webTracking.rotatedTitle"), description: t("operations:webTracking.rotatedBody") });
    } catch {
      toast({ title: t("operations:webTracking.actionFailedTitle"), variant: "destructive" });
    } finally { setBusy(null); }
  }

  async function removeSite() {
    setBusy("remove");
    try {
      await apiRequest("DELETE", "/api/website-tracking/site");
      setConfirmRemove(false);
      setVerifyResult(null);
      await qc.invalidateQueries({ queryKey: ["/api/website-tracking/site"] });
      toast({ title: t("operations:webTracking.removedTitle"), description: t("operations:webTracking.removedBody") });
    } catch {
      toast({ title: t("operations:webTracking.actionFailedTitle"), variant: "destructive" });
    } finally { setBusy(null); }
  }

  if (isLoading) return null;

  return (
    <Card data-testid="card-website-tracking">
      <CardHeader className="pb-3 pt-4">
        <CardTitle className="text-sm flex items-center gap-2">
          <Globe className="w-4 h-4 text-primary" />
          {t("operations:webTracking.title")}
          {site && (
            site.status === "connected"
              ? <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400 border-emerald-500/30 gap-1"><CheckCircle2 className="w-3 h-3" />{t("operations:webTracking.statusConnected")}</Badge>
              : site.status === "stale"
              ? <Badge variant="outline" className="text-amber-600 dark:text-amber-400 border-amber-500/30 gap-1"><AlertTriangle className="w-3 h-3" />{t("operations:webTracking.statusStale")}</Badge>
              : site.status === "revoked"
              ? <Badge variant="outline" className="text-destructive border-destructive/30 gap-1"><AlertTriangle className="w-3 h-3" />{t("operations:webTracking.statusRevoked")}</Badge>
              : <Badge variant="outline" className="text-amber-600 dark:text-amber-400 border-amber-500/30 gap-1"><Clock className="w-3 h-3" />{t("operations:webTracking.statusWaiting")}</Badge>
          )}
        </CardTitle>
        <CardDescription>{t("operations:webTracking.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">

        {/* ── Not set up yet: explainer + sample preview + URL form ─────────── */}
        {!site && (
          <>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>{t("operations:webTracking.explainer1")}</p>
              <p>{t("operations:webTracking.explainer2")}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">{t("operations:webTracking.enterUrlLabel")}</p>
              <div className="flex gap-2 max-w-lg">
                <Input
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://www.yourdealership.com"
                  data-testid="input-wt-website-url"
                  onKeyDown={(e) => { if (e.key === "Enter") createSite(); }}
                />
                <Button onClick={createSite} disabled={!urlInput.trim() || busy === "create"} data-testid="button-wt-create" className="gap-1.5 shrink-0">
                  {busy === "create" ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                  {t("operations:webTracking.getSnippetBtn")}
                </Button>
              </div>
            </div>
            <div>
              <button
                onClick={() => setShowExample((v) => !v)}
                data-testid="button-wt-see-example"
                className="flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <BarChart3 className="w-4 h-4" />
                {showExample ? t("operations:webTracking.hideExample") : t("operations:webTracking.seeExample")}
              </button>
              {showExample && (
                <div className="rounded-lg border border-dashed border-border/70 p-4 overflow-hidden mt-3">
                  <div className="flex justify-end mb-3">
                    <Badge variant="secondary">{t("operations:webTracking.sampleBadge")}</Badge>
                  </div>
                  <div className="opacity-70">
                    <AnalyticsDashboard data={FAKE_SUMMARY} sample />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Site exists: snippet + actions + status ───────────────────────── */}
        {site && (
          <>
            {/* Verify result / troubleshooting */}
            {verifyResult && (
              <div className="rounded-lg border border-border/60 bg-muted/30 px-4 py-3 space-y-1.5 text-xs" data-testid="text-wt-verify-result">
                <p className="font-medium text-sm">
                  {verifyResult.status === "connected"
                    ? t("operations:webTracking.verifyConnected", { count: verifyResult.eventsLast24h })
                    : verifyResult.status === "stale"
                    ? t("operations:webTracking.verifyStale")
                    : verifyResult.status === "revoked"
                    ? t("operations:webTracking.verifyRevoked")
                    : t("operations:webTracking.verifyNoEvents")}
                </p>
                <p>{t("operations:webTracking.verifyDomain", { domain: verifyResult.approvedDomain })}</p>
                {verifyResult.lastEventAt && (
                  <p>{t("operations:webTracking.lastEvent", { time: new Date(verifyResult.lastEventAt).toLocaleString() })}</p>
                )}
                {verifyResult.lastRejectedReason && (
                  <p className="text-amber-600 dark:text-amber-400">
                    {verifyResult.lastRejectedReason.startsWith("domain_mismatch")
                      ? t("operations:webTracking.rejectDomainMismatch", { detail: verifyResult.lastRejectedReason.split(":")[1] ?? "" })
                      : t("operations:webTracking.rejectRevokedKey")}
                    {verifyResult.lastRejectedAt ? ` (${new Date(verifyResult.lastRejectedAt).toLocaleString()})` : ""}
                  </p>
                )}
                {verifyResult.status !== "connected" && (
                  <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
                    <li>{t("operations:webTracking.trouble1")}</li>
                    <li>{t("operations:webTracking.trouble2")}</li>
                    <li>{t("operations:webTracking.trouble3")}</li>
                    <li>{t("operations:webTracking.trouble4")}</li>
                    <li>{t("operations:webTracking.trouble5")}</li>
                  </ul>
                )}
              </div>
            )}

            {/* Platform tips */}
            {site.status !== "connected" && (
              <div className="text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">{t("operations:webTracking.platformTipsTitle")}</p>
                <p>{t("operations:webTracking.platformTipGeneric")}</p>
                <p>{t("operations:webTracking.platformTipDealerCom")}</p>
                <p>{t("operations:webTracking.platformTipWordpress")}</p>
                <p>{t("operations:webTracking.platformTipWixSquarespace")}</p>
              </div>
            )}

            {/* Connected analytics (kept visible while stale — history still matters) */}
            {(site.status === "connected" || site.status === "stale") && (
              <div className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-sm font-medium">{t("operations:webTracking.analyticsTitle")}</p>
                  <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5">
                    {([7, 30, 90, "12m"] as ChartRange[]).map((r) => (
                      <button
                        key={r}
                        onClick={() => setRange(r)}
                        data-testid={`button-wt-days-${r}`}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${range === r ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      >
                        {r === "12m" ? t("operations:webTracking.last12Months") : t("operations:webTracking.lastNDays", { n: r })}
                      </button>
                    ))}
                  </div>
                </div>
                {summary ? <AnalyticsDashboard data={summary} range={range} /> : <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
              </div>
            )}

            {/* Tracking code — always visible at the bottom, always copyable */}
            <div className="space-y-2 pt-2 border-t border-border/50">
              <p className="text-sm font-medium">{t("operations:webTracking.snippetLabel", { domain: site.approvedDomain })}</p>
              <code className="block text-[11px] bg-muted/60 border border-border/60 rounded-md px-3 py-2.5 break-all" data-testid="text-wt-snippet">
                {snippet}
              </code>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={copyCode} data-testid="button-wt-copy" className="gap-1.5">
                  <Copy className="w-3.5 h-3.5" />{t("operations:webTracking.copyBtn")}
                </Button>
                <Button size="sm" variant="outline" onClick={emailProvider} data-testid="button-wt-email" className="gap-1.5">
                  <Mail className="w-3.5 h-3.5" />{t("operations:webTracking.emailBtn")}
                </Button>
                <Button size="sm" variant="outline" onClick={verifyInstall} disabled={busy === "verify"} data-testid="button-wt-verify" className="gap-1.5">
                  {busy === "verify" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  {t("operations:webTracking.verifyBtn")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{t("operations:webTracking.installInstructions")}</p>
            </div>

            {/* Advanced actions */}
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border/50">
              <button
                onClick={rotateKey}
                disabled={busy === "rotate"}
                data-testid="button-wt-rotate"
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                <KeyRound className="w-3.5 h-3.5" />{t("operations:webTracking.rotateBtn")}
              </button>
              {!confirmRemove ? (
                <button
                  onClick={() => setConfirmRemove(true)}
                  data-testid="button-wt-remove"
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />{t("operations:webTracking.removeBtn")}
                </button>
              ) : (
                <span className="flex items-center gap-2 text-xs">
                  {t("operations:webTracking.removeConfirm")}
                  <Button size="sm" variant="destructive" className="h-6 px-2 text-xs" onClick={removeSite} disabled={busy === "remove"} data-testid="button-wt-remove-confirm">
                    {t("operations:webTracking.removeYes")}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setConfirmRemove(false)} data-testid="button-wt-remove-cancel">
                    {t("common:cancel")}
                  </Button>
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
